import { rem } from 'polished';
import { css } from 'styled-components';

import type { Spacing, Theme } from '../theme/types';
import { isSpacingKey } from '../theme/utils';
import type { Styled } from '../types/common.types';

export type BaseFlexProps = {
  /** Shorthand for `alignItems` style prop */
  align: React.CSSProperties['alignItems'];

  /** Shorthand for `justifyContent` style prop */
  justify: React.CSSProperties['justifyContent'];

  /** Shorthand for `flexWrap` style prop */
  wrap: React.CSSProperties['flexWrap'] | boolean;

  /** Shorthand for `flexDirection` style prop */
  direction: React.CSSProperties['flexDirection'];

  /** Shorthand for `flexBasis` style prop */
  basis: React.CSSProperties['flexBasis'];

  /** Shorthand for `flexGrow` style prop */
  grow: React.CSSProperties['flexGrow'];

  /** Shorthand for `flexShrink` style prop */
  shrink: React.CSSProperties['flexShrink'];

  gap: number | undefined | keyof Spacing | (string & {});
};

export type FlexProps = Partial<BaseFlexProps>;

export type StyledFlexProps = Styled<FlexProps>;

const gapValue = (
  theme: Theme,
  value: BaseFlexProps['gap'],
): string | undefined => {
  if (typeof value === 'number') {
    return rem(value);
  }

  if (!value) return undefined;

  if (isSpacingKey(theme, value)) {
    return theme.core.spacing[value];
  }

  return value;
};

/**
 * Styles that can map flex props prefixed with `$` to an adequate CSS property
 *
 * @example
 * const Test = styled.div<StyledFlexProps>`
 *   ${flexStyles}
 * `
 */
export const flexStyles = css<StyledFlexProps>(
  ({
    theme,
    $align,
    $justify,
    $wrap,
    $direction,
    $basis,
    $grow,
    $shrink,
    $gap,
  }) => ({
    alignItems: $align,
    justifyContent: $justify,
    flexWrap: typeof $wrap === 'boolean' ? 'wrap' : $wrap,
    flexDirection: $direction,
    flexBasis: $basis,
    flexGrow: $grow,
    flexShrink: $shrink,
    gap: gapValue(theme, $gap),
  }),
);

export const extractFlexProps = <T extends FlexProps>(
  props: T,
): BaseFlexProps => {
  const { align, justify, wrap, direction, basis, grow, shrink, gap } = props;

  const extracted: BaseFlexProps = {
    align,
    justify,
    wrap,
    direction,
    basis,
    grow,
    shrink,
    gap,
  };

  return extracted;
};
